import React, { useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Card } from '../../components/ui/Card';
import { LineChart } from '../../components/charts';
import {
  ArrowUp,
  DollarSign,
  Globe,
  RefreshCw,
  Search,
  ShoppingBag,
  Users,
  X
} from 'lucide-react';
import { isDemoBrand, demoHash } from '../../utils/demoBrand';

// Static raw Adjust performance rows for demo brands. The page's own
// processAdjustSourceData/processAllMonthlyChartsData digest these, so we only
// need the raw per-day-per-campaign shape they read.
function buildDemoAdjustRows() {
  const channels = [
    { channel: 'Organic', campaigns: ['organic'] },
    { channel: 'Google Ads', campaigns: ['UAC - Install', 'UAC - Value'] },
    { channel: 'Meta Ads', campaigns: ['AAA - Prospecting', 'Retargeting'] },
    { channel: 'TikTok', campaigns: ['Video Views', 'Conversions'] },
    { channel: 'Apple Search Ads', campaigns: ['Brand', 'Generic'] },
    { channel: 'Instagram', campaigns: ['Stories', 'Reels'] },
  ];
  const rows = [];
  const today = new Date();
  const DAYS = 180;
  for (const ch of channels) {
    const isOrganic = ch.channel === 'Organic';
    for (const camp of ch.campaigns) {
      for (let i = 0; i < DAYS; i++) {
        const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
        const ds = d.toISOString().slice(0, 10);
        const h = demoHash(ch.channel + camp + ds);
        const sessions = (isOrganic ? 400 : 120) + (h % (isOrganic ? 1200 : 600));
        const installs = Math.round(sessions * (0.12 + (h % 20) / 100));
        const purchase_events = Math.round(installs * (0.08 + (h % 25) / 100));
        rows.push({
          channel: ch.channel,
          campaign: camp,
          date: ds,
          sessions,
          installs,
          purchase_events,
          all_revenue: Math.round(purchase_events * (300 + (h % 500))),
        });
      }
    }
  }
  return rows;
}

export default function AdjustSourceMediumMonthly() {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('last30');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [sourceAnalytics, setSourceAnalytics] = useState({
    summary: {
      total_sessions: 0,
      total_users: 0,
      total_installs: 0,
      conversion_rate: 0,
      avg_session_duration: '00:00',
      bounce_rate: 0,
      revenue: 0
    },
    sources: []
  });

  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const [primaryMetric, setPrimaryMetric] = useState('revenue'); // 'sessions' | 'installs' | 'conversions' | 'revenue' | 'cr'
  const [secondaryMetric, setSecondaryMetric] = useState('none');

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Sessions',
        data: [],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y'
      },
      {
        label: 'Conversions',
        data: [],
        borderColor: 'rgb(234, 88, 12)',
        backgroundColor: 'rgba(234, 88, 12, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y'
      },
      {
        label: 'Revenue',
        data: [],
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y1'
      },
      {
        label: 'CR (%)',
        data: [],
        borderColor: 'rgb(245, 101, 101)',
        backgroundColor: 'rgba(245, 101, 101, 0.1)',
        fill: false,
        tension: 0.4,
        yAxisID: 'y2'
      }
    ]
  });

  // All-time monthly charts (Sessions, Purchases, Revenue, CR) – independent of dateRange
  const [allMonthlyChartsData, setAllMonthlyChartsData] = useState({
    sessions: { labels: [], data: [] },
    purchases: { labels: [], data: [] },
    revenue: { labels: [], data: [] },
    conversionRate: { labels: [], data: [] }
  });

  // Store all-time raw rows so we can re-filter by selected channel
  const [allRows, setAllRows] = useState([]);

  // Monthly charts (Sessions, Purchases, Revenue, CR) similar to GA analytics page
  const [monthlyChartsData, setMonthlyChartsData] = useState({
    sessions: { labels: [], data: [] },
    purchases: { labels: [], data: [] },
    revenue: { labels: [], data: [] },
    conversionRate: { labels: [], data: [] }
  });

  const formatDuration = (seconds) => {
    if (!seconds) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDateForAPI = (date) => {
    if (!date) return null;
    const adjustedDate = new Date(date);
    const year = adjustedDate.getFullYear();
    const month = String(adjustedDate.getMonth() + 1).padStart(2, '0');
    const day = String(adjustedDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDateRangeForFilter = (filter) => {
    const today = new Date();
    let startDate, endDate;

    switch (filter) {
      case 'today':
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
        break;
      case 'yesterday':
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1, 23, 59, 59);
        break;
      case 'thisMonth':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = today;
        break;
      case 'lastMonth':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59);
        break;
      case 'last7':
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
        endDate = today;
        break;
      case 'last30':
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
        endDate = today;
        break;
      case 'last90':
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 90);
        endDate = today;
        break;
      case 'all':
        startDate = new Date(2020, 0, 1);
        endDate = today;
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59, 999);
        } else {
          startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
          endDate = today;
        }
        break;
      default:
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
        endDate = today;
    }

    return { startDate, endDate };
  };

  const selectedRangeLabel = useMemo(() => {
    const r = getDateRangeForFilter(dateRange);
    if (!r || !r.startDate || !r.endDate) return '';
    return `${r.startDate.toLocaleDateString('tr-TR')} - ${r.endDate.toLocaleDateString('tr-TR')}`;
  }, [dateRange, customStartDate, customEndDate]);

  const filterDataByDateRange = (data, filter) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }

    const { startDate, endDate } = getDateRangeForFilter(filter);

    return data.filter((item) => {
      if (!item.date) return false;
      const itemDate = new Date(item.date);
      return itemDate >= startDate && itemDate <= endDate;
    });
  };

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      setDateRange('custom');
      setIsCustomDateOpen(false);
    }
  };

  const handleCustomDateReset = () => {
    setCustomStartDate(null);
    setCustomEndDate(null);
    setDateRange('last30');
    setIsCustomDateOpen(false);
  };

  const formatCompactCurrency = (value) => {
    const n = Number(value) || 0;
    if (n >= 1_000_000_000) return `₺${(n / 1_000_000_000).toFixed(1)}B`;
    if (n >= 1_000_000) return `₺${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `₺${Math.round(n / 1_000)}K`;
    return `₺${n.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`;
  };

  const formatCompactNumber = (value) => {
    const n = Number(value) || 0;
    if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
    return n.toLocaleString('tr-TR');
  };

  const processAdjustSourceData = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      setSourceAnalytics({
        summary: {
          total_sessions: 0,
          total_users: 0,
          total_installs: 0,
          conversion_rate: 0,
          avg_session_duration: '00:00',
          bounce_rate: 0,
          revenue: 0
        },
        sources: []
      });
      setChartData((prev) => ({
        ...prev,
        labels: [],
        datasets: prev.datasets.map((ds) => ({ ...ds, data: [] }))
      }));
      setLoading(false);
      return;
    }

    const filteredData = filterDataByDateRange(rows, dateRange);

    const groupsMap = {};

    filteredData.forEach((item) => {
      const sourceLabel = item.channel || item.source_network || 'Unknown';
      const key = sourceLabel.toLowerCase();

      if (!groupsMap[key]) {
        groupsMap[key] = {
          key,
          source: sourceLabel,
          sessions: 0,
          installs: 0,
          conversions: 0,
          revenue: 0,
          dailyData: {},
          campaigns: {},
          type: 'paid',
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600'
        };
      }

      const g = groupsMap[key];
      const sessions = Number(item.sessions || 0);
      const installs = Number(item.installs || 0);
      const conversions = Number(item.purchase_events || item.events || 0);
      const revenue = Number(item.all_revenue ?? item.revenue ?? 0);

      g.sessions += sessions;
      g.installs += installs;
      g.conversions += conversions;
      g.revenue += revenue;

      const date = item.date ? new Date(item.date) : null;
      const dateKey = date ? date.toISOString().split('T')[0] : null;

      if (dateKey) {
        if (!g.dailyData[dateKey]) {
          g.dailyData[dateKey] = {
            date: dateKey,
            sessions: 0,
            installs: 0,
            conversions: 0,
            revenue: 0
          };
        }
        g.dailyData[dateKey].sessions += sessions;
        g.dailyData[dateKey].installs += installs;
        g.dailyData[dateKey].conversions += conversions;
        g.dailyData[dateKey].revenue += revenue;
      }

      const campaignName = item.campaign || '(not set)';
      if (!g.campaigns[campaignName]) {
        g.campaigns[campaignName] = {
          sessions: 0,
          installs: 0,
          conversions: 0,
          revenue: 0,
          dailyData: {}
        };
      }
      const c = g.campaigns[campaignName];
      c.sessions += sessions;
      c.installs += installs;
      c.conversions += conversions;
      c.revenue += revenue;
      if (dateKey) {
        if (!c.dailyData[dateKey]) {
          c.dailyData[dateKey] = {
            date: dateKey,
            sessions: 0,
            installs: 0,
            conversions: 0,
            revenue: 0
          };
        }
        c.dailyData[dateKey].sessions += sessions;
        c.dailyData[dateKey].installs += installs;
        c.dailyData[dateKey].conversions += conversions;
        c.dailyData[dateKey].revenue += revenue;
      }
    });

    const processedSources = Object.values(groupsMap)
      .map((group) => {
        const dailyData = Object.values(group.dailyData).sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );

        const campaignsArray = Object.entries(group.campaigns)
          .map(([name, metrics]) => ({
            name,
            sessions: metrics.sessions,
            installs: metrics.installs,
            conversions: metrics.conversions,
            revenue: metrics.revenue,
            dailyData: Object.values(metrics.dailyData).sort(
              (a, b) => new Date(a.date) - new Date(b.date)
            ),
            conversion_rate:
              metrics.sessions > 0
                ? ((metrics.conversions / metrics.sessions) * 100).toFixed(2)
                : '0.00'
          }))
          .sort((a, b) => b.sessions - a.sessions);

        const conversionRate =
          group.sessions > 0 ? (group.conversions / group.sessions) * 100 : 0;

        return {
          key: group.key,
          source: group.source,
          sessions: group.sessions,
          users: group.sessions,
          installs: group.installs,
          new_users: 0,
          conversions: group.conversions,
          revenue: group.revenue,
          conversion_rate: conversionRate.toFixed(2),
          trend: 'up',
          trend_percentage: '0.0',
          dailyData,
          campaigns: campaignsArray,
          performance: 'Medium',
          type: group.type,
          iconBg: group.iconBg,
          iconColor: group.iconColor
        };
      })
      .sort((a, b) => b.sessions - a.sessions);

    const totals = {
      sessions: processedSources.reduce((sum, s) => sum + s.sessions, 0),
      installs: processedSources.reduce((sum, s) => sum + (s.installs || 0), 0),
      conversions: processedSources.reduce((sum, s) => sum + s.conversions, 0),
      revenue: processedSources.reduce((sum, s) => sum + s.revenue, 0)
    };
    const totalConversionRate =
      totals.sessions > 0 ? (totals.conversions / totals.sessions) * 100 : 0;

    setSourceAnalytics({
      summary: {
        total_sessions: totals.sessions,
        total_users: totals.sessions,
        total_installs: totals.installs,
        conversion_rate: totalConversionRate.toFixed(1),
        avg_session_duration: formatDuration(0),
        bounce_rate: 0,
        revenue: totals.revenue
      },
      sources: processedSources
    });
    updateChartData(null, null, filteredData);
    setLoading(false);
  };

  // Build all-time monthly charts data (ignores current dateRange)
  const processAllMonthlyChartsData = (rows, selectedSourceArg = null) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      setAllMonthlyChartsData({
        sessions: { labels: [], data: [] },
        purchases: { labels: [], data: [] },
        revenue: { labels: [], data: [] },
        conversionRate: { labels: [], data: [] }
      });
      return;
    }

    let workingRows = rows;
    if (selectedSourceArg && selectedSourceArg.source) {
      const target = String(selectedSourceArg.source || '').toLowerCase();
      workingRows = rows.filter((item) => {
        const label = (item.channel || item.source_network || 'unknown').toLowerCase();
        return label === target;
      });
    }

    const monthlyData = {};
    workingRows.forEach((item) => {
      if (!item.date) return;
      const d = new Date(item.date);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          date: monthKey,
          sessions: 0,
          purchases: 0,
          revenue: 0
        };
      }
      monthlyData[monthKey].sessions += Number(item.sessions || 0);
      monthlyData[monthKey].purchases += Number(item.purchase_events || item.events || 0);
      monthlyData[monthKey].revenue += Number(item.all_revenue ?? item.revenue ?? 0);
    });

    const sortedMonths = Object.values(monthlyData).sort((a, b) =>
      String(a.date).localeCompare(String(b.date))
    );
    const monthLabels = sortedMonths.map((m) => {
      const [year, monthNum] = String(m.date).split('-');
      const d = new Date(Number(year), Number(monthNum) - 1);
      return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    });
    const monthSessions = sortedMonths.map((m) => m.sessions);
    const monthPurchases = sortedMonths.map((m) => m.purchases);
    const monthRevenue = sortedMonths.map((m) => m.revenue);
    const monthCR = sortedMonths.map((m) =>
      m.sessions > 0 ? ((m.purchases / m.sessions) * 100).toFixed(2) : 0
    );

    setAllMonthlyChartsData({
      sessions: { labels: monthLabels, data: monthSessions },
      purchases: { labels: monthLabels, data: monthPurchases },
      revenue: { labels: monthLabels, data: monthRevenue },
      conversionRate: { labels: monthLabels, data: monthCR }
    });
  };

  const updateChartData = (source, campaign, filteredData = null) => {
    let dateData = [];
    let chartTitlePrefix = 'Overall';

    if (campaign && source) {
      dateData = campaign.dailyData || [];
      chartTitlePrefix = `${source.source} > ${campaign.name}`;
    } else if (source) {
      dateData = source.dailyData || [];
      chartTitlePrefix = source.source;
    } else if (filteredData) {
      const dateMap = {};

      filteredData.forEach((item) => {
        if (!item.date) return;
        const date = new Date(item.date);
        const dateKey = date.toISOString().split('T')[0];

        if (!dateMap[dateKey]) {
          dateMap[dateKey] = {
            date: dateKey,
            sessions: 0,
            installs: 0,
            conversions: 0,
            revenue: 0
          };
        }

        const installs = Number(item.installs || 0);
        dateMap[dateKey].sessions += Number(item.sessions) || 0;
        dateMap[dateKey].installs += installs;
        dateMap[dateKey].conversions +=
          Number(item.purchase_events || item.events) || 0;
        dateMap[dateKey].revenue +=
          Number(item.all_revenue ?? item.revenue ?? 0) || 0;
      });

      dateData = Object.values(dateMap).sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );
      chartTitlePrefix = 'All Sources';
    }

    if (dateData.length === 0) {
      setChartData((prev) => ({
        ...prev,
        labels: [],
        datasets: prev.datasets.map((ds) => ({ ...ds, data: [] }))
      }));
      return;
    }

    const labels = dateData.map((d) => {
      const date = new Date(d.date);
      return `${date.getDate()} ${date.toLocaleString('default', {
        month: 'short'
      })}`;
    });

    const sessions = dateData.map((d) => d.sessions);
    const installs = dateData.map((d) => d.installs || 0);
    const conversions = dateData.map((d) => d.conversions);
    const revenue = dateData.map((d) => d.revenue);
    const conversionRate = dateData.map((d) =>
      d.sessions > 0 ? (d.conversions / d.sessions) * 100 : 0
    );

    const getSeriesConfig = (metric, axis) => {
      if (metric === 'sessions') {
        return {
          label: `${chartTitlePrefix} - Sessions`,
          data: sessions,
          borderColor: 'rgb(59,130,246)',
          backgroundColor: 'rgba(59,130,246,0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: axis
        };
      }
      if (metric === 'installs') {
        return {
          label: `${chartTitlePrefix} - Installs`,
          data: installs,
          borderColor: 'rgb(99,102,241)',
          backgroundColor: 'rgba(99,102,241,0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: axis
        };
      }
      if (metric === 'conversions') {
        return {
          label: `${chartTitlePrefix} - Conversions`,
          data: conversions,
          borderColor: 'rgb(234,88,12)',
          backgroundColor: 'rgba(234,88,12,0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: axis
        };
      }
      if (metric === 'revenue') {
        return {
          label: `${chartTitlePrefix} - Revenue`,
          data: revenue,
          borderColor: 'rgb(16,185,129)',
          backgroundColor: 'rgba(16,185,129,0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: axis
        };
      }
      return {
        label: `${chartTitlePrefix} - CR (%)`,
        data: conversionRate,
        borderColor: 'rgb(245,101,101)',
        backgroundColor: 'rgba(245,101,101,0.1)',
        fill: false,
        tension: 0.4,
        yAxisID: axis
      };
    };

    const primary = primaryMetric || 'revenue';
    const secondary = secondaryMetric || 'none';
    const datasets = [];
    datasets.push(getSeriesConfig(primary, 'y'));
    if (secondary !== 'none' && secondary !== primary) {
      datasets.push(getSeriesConfig(secondary, 'y1'));
    }

    setChartData({
      labels,
      datasets
    });
  };

  const handleSourceClick = (source) => {
    setSelectedSource(source);
    setSelectedCampaign(null);
  };

  const handleTotalClick = () => {
    setSelectedSource(null);
    setSelectedCampaign(null);
  };

  const fetchAdjustData = async () => {
    try {
      setLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        console.log('No brand ID found in localStorage (currentBrandId)');
        setLoading(false);
        return;
      }

      const { startDate, endDate } = getDateRangeForFilter(dateRange);
      const params = new URLSearchParams();
      if (startDate) params.set('startDate', formatDateForAPI(startDate));
      if (endDate) params.set('endDate', formatDateForAPI(endDate));
      params.set('limit', '50000');

      const url = `https://api.glowytics.com/api/adjust/performance?${params.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const js = await response.json();
      const rows =
        js && js.ok && Array.isArray(js.rows)
          ? js.rows
          : Array.isArray(js.rows)
            ? js.rows
            : Array.isArray(js.data)
              ? js.data
              : [];

      let finalRows = rows || [];
      if (finalRows.length === 0 && isDemoBrand(brandId)) finalRows = buildDemoAdjustRows();
      processAdjustSourceData(finalRows);
    } catch (err) {
      console.error('❌ Error fetching Adjust source data:', err);
      if (isDemoBrand(localStorage.getItem('currentBrandId'))) {
        processAdjustSourceData(buildDemoAdjustRows());
      } else {
        setLoading(false);
      }
    }
  };

  // All-time fetch for monthly mini charts (no date filter)
  const fetchAdjustDataAllTime = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        console.log('No brand ID found in localStorage (currentBrandId)');
        return;
      }
      // Use main performance endpoint with an explicit "all time" date range
      const today = new Date();
      const params = new URLSearchParams();
      params.set('startDate', '2020-01-01');
      params.set('endDate', formatDateForAPI(today));
      // Request high cap so backend doesn't truncate older history
      params.set('limit', '2000000');
      const url = `https://api.glowytics.com/api/adjust/performance?${params.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`API error (all-time): ${response.status} ${response.statusText}`);
      }
      const js = await response.json();
      const rows =
        js && js.ok && Array.isArray(js.rows)
          ? js.rows
          : Array.isArray(js.rows)
            ? js.rows
            : Array.isArray(js.data)
              ? js.data
              : [];
      let safeRows = rows || [];
      if (safeRows.length === 0 && isDemoBrand(brandId)) safeRows = buildDemoAdjustRows();
      setAllRows(safeRows);
      processAllMonthlyChartsData(safeRows, selectedSource);
    } catch (err) {
      console.error('❌ Error fetching Adjust all-time data for monthly charts:', err);
      if (isDemoBrand(localStorage.getItem('currentBrandId'))) {
        const demoRows = buildDemoAdjustRows();
        setAllRows(demoRows);
        processAllMonthlyChartsData(demoRows, selectedSource);
      }
    }
  };

  useEffect(() => {
    fetchAdjustData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, customStartDate, customEndDate]);

  // All-time monthly charts: fetch once on mount
  useEffect(() => {
    fetchAdjustDataAllTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recompute all-time monthly charts when selected channel changes
  useEffect(() => {
    if (allRows && allRows.length > 0) {
      processAllMonthlyChartsData(allRows, selectedSource);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSource, allRows]);

  useEffect(() => {
    if (selectedCampaign && selectedSource) {
      updateChartData(selectedSource, selectedCampaign);
    } else if (selectedSource) {
      updateChartData(selectedSource, null);
    } else {
      // overall
      // we don't store raw rows here, but processAdjustSourceData already called updateChartData
    }
  }, [selectedSource, selectedCampaign, primaryMetric, secondaryMetric]);

  if (loading && !sourceAnalytics.sources.length) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="loading-logo-container">
          <img src="/logoicon.png" alt="Glowytics Logo" className="loading-logo" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-4">
            <img
              src="/adjustlogopng.png"
              alt="Adjust"
              className="w-12 h-12 object-contain"
            />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Adjust • Traffic Acquisition
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 bg-gray-50 p-0.5 rounded-lg">
              <button
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'yesterday'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setDateRange('yesterday')}
              >
                Yesterday
              </button>
              <button
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'last7'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setDateRange('last7')}
              >
                7 Days
              </button>
              <button
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'last30'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setDateRange('last30')}
              >
                30 Days
              </button>
              <button
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'thisMonth'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setDateRange('thisMonth')}
              >
                This Month
              </button>
              <button
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'lastMonth'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setDateRange('lastMonth')}
              >
                Last Month
              </button>
              <button
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'last90'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setDateRange('last90')}
              >
                90 Days
              </button>
              <button
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'all'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setDateRange('all')}
              >
                All Data
              </button>
              <button
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'custom'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setIsCustomDateOpen(true)}
              >
                Custom Range
              </button>
            </div>

            <button
              className="p-2 text-gray-600 hover:text-blue-600 rounded-xl hover:bg-blue-50 transition-colors"
              onClick={fetchAdjustData}
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-6 gap-4">
          <Card className="p-4 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-xl border border-blue-400">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-blue-100 text-xs">Total Sessions</p>
                <p className="text-xl font-semibold mt-1">
                  {sourceAnalytics.summary.total_sessions.toLocaleString()}
                </p>
                <div className="flex items-center mt-2 text-blue-100 text-xs">
                  <ArrowUp className="w-3 h-3 mr-1" />
                  <span>–</span>
                </div>
              </div>
              <div className="p-2 bg-white/10 rounded-lg">
                <Users className="w-4 h-4" />
              </div>
            </div>
          </Card>

          <Card className="p-4 rounded-xl shadow-xl border border-gray-100">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-xs">Installs</p>
                <p className="text-xl font-semibold mt-1 text-gray-900">
                  {sourceAnalytics.summary.total_installs.toLocaleString()}
                </p>
                <div className="flex items-center mt-2 text-green-600 text-xs">
                  <ArrowUp className="w-3 h-3 mr-1" />
                  <span>–</span>
                </div>
              </div>
              <div className="p-2 bg-blue-50 rounded-lg">
                <Globe className="w-4 h-4 text-blue-600" />
              </div>
            </div>
          </Card>

          <Card className="p-4 rounded-xl shadow-xl border border-gray-100">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-xs">Total Purchases</p>
                <p className="text-xl font-semibold mt-1 text-gray-900">
                  {Math.round(
                    sourceAnalytics.summary.total_sessions *
                      (sourceAnalytics.summary.conversion_rate / 100)
                  ).toLocaleString()}
                </p>
                <div className="flex items-center mt-2 text-green-600 text-xs">
                  <ArrowUp className="w-3 h-3 mr-1" />
                  <span>–</span>
                </div>
              </div>
              <div className="p-2 bg-purple-50 rounded-lg">
                <ShoppingBag className="w-4 h-4 text-purple-600" />
              </div>
            </div>
          </Card>

          <Card className="p-4 rounded-xl shadow-xl border border-gray-100">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-xs">Conversion Rate</p>
                <p className="text-xl font-semibold mt-1 text-gray-900">
                  {sourceAnalytics.summary.conversion_rate}%
                </p>
                <div className="flex items-center mt-2 text-green-600 text-xs">
                  <ArrowUp className="w-3 h-3 mr-1" />
                  <span>–</span>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4 rounded-xl shadow-xl border border-gray-100">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-xs">Revenue per Conversion</p>
                <p className="text-xl font-semibold mt-1 text-gray-900">
                  ₺
                  {sourceAnalytics.summary.revenue /
                    (sourceAnalytics.summary.total_sessions *
                      (sourceAnalytics.summary.conversion_rate / 100)) >
                  0
                    ? (
                        sourceAnalytics.summary.revenue /
                        (sourceAnalytics.summary.total_sessions *
                          (sourceAnalytics.summary.conversion_rate / 100))
                      ).toFixed(2)
                    : '0.00'}
                </p>
                <div className="flex items-center mt-2 text-green-600 text-xs">
                  <ArrowUp className="w-3 h-3 mr-1" />
                  <span>–</span>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4 rounded-xl shadow-xl border border-gray-100">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-xs">Total Revenue</p>
                <p className="text-xl font-semibold mt-1 text-gray-900">
                  ₺
                  {sourceAnalytics.summary.revenue.toLocaleString('tr-TR', {
                    maximumFractionDigits: 0
                  })}
                </p>
                <div className="flex items-center mt-2 text-green-600 text-xs">
                  <ArrowUp className="w-3 h-3 mr-1" />
                  <span>–</span>
                </div>
              </div>
              <div className="p-2 bg-blue-50 rounded-lg">
                <DollarSign className="w-4 h-4 text-blue-600" />
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-4">
          <Card className="border border-gray-200 rounded-xl shadow-sm overflow-hidden h-full">
            <div className="p-4 border-b">
              <h2 className="font-semibold text-sm text-gray-800">Channels</h2>
              <div className="mt-3">
                <div className="relative w-full">
                  <input
                    type="text"
                    placeholder="Search sources..."
                    className="pl-9 pr-4 py-2 bg-gray-50 border rounded-lg text-sm w-full"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
              </div>
            </div>

            <div className="overflow-x-auto overflow-y-auto max-h-[900px]">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="pl-3 pr-3 py-1.5 text-[9px] font-medium text-gray-500">
                      CHANNEL
                    </th>
                    <th className="px-3 py-1.5 text-[9px] font-medium text-gray-500">
                      SESSIONS
                    </th>
                    <th className="px-3 py-1.5 text-[9px] font-medium text-gray-500">
                      INSTALLS
                    </th>
                    <th className="px-3 py-1.5 text-[9px] font-medium text-gray-500">
                      Conv.
                    </th>
                    <th className="px-3 py-1.5 text-[9px] font-medium text-gray-500">
                      CR
                    </th>
                    <th className="px-3 py-1.5 text-[9px] font-medium text-gray-500">
                      REVENUE
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {sourceAnalytics.sources
                    .filter((source) =>
                      source.source.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .map((source) => (
                      <tr
                        key={source.key}
                        className={`hover:bg-gray-50 transition-colors ${
                          selectedSource && selectedSource.key === source.key
                            ? 'bg-blue-50'
                            : ''
                        }`}
                      >
                        <td
                          className="pl-3 pr-3 py-1 whitespace-normal"
                          onClick={() => handleSourceClick(source)}
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 flex-shrink-0"></div>
                            <div className="flex items-center gap-2">
                              <div
                                className={`p-1.5 rounded-md ${source.iconBg || 'bg-gray-100'}`}
                              >
                                <div
                                  className={`w-3 h-3 rounded-full ${
                                    source.iconColor || 'text-gray-600'
                                  }`}
                                  style={{ backgroundColor: 'currentColor' }}
                                ></div>
                              </div>
                              <span className="font-medium text-[10px] text-gray-900 cursor-pointer break-words leading-tight min-w-0">
                                {source.source}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td
                          className="px-3 py-1 whitespace-nowrap cursor-pointer"
                          onClick={() => handleSourceClick(source)}
                        >
                          <div className="font-medium text-[10px] text-gray-900">
                            {formatCompactNumber(source.sessions)}
                          </div>
                        </td>
                        <td
                          className="px-3 py-1 whitespace-nowrap cursor-pointer"
                          onClick={() => handleSourceClick(source)}
                        >
                          <div className="font-medium text-[10px] text-gray-900">
                            {formatCompactNumber(source.installs || 0)}
                          </div>
                        </td>
                        <td
                          className="px-3 py-1 whitespace-nowrap cursor-pointer"
                          onClick={() => handleSourceClick(source)}
                        >
                          <div className="font-medium text-[10px] text-gray-900">
                            {source.conversions.toLocaleString()}
                          </div>
                        </td>
                        <td
                          className="px-3 py-1 whitespace-nowrap cursor-pointer"
                          onClick={() => handleSourceClick(source)}
                        >
                          <div
                            className={`font-medium text-[10px] ${
                              Number(source.conversion_rate) >= 3
                                ? 'text-green-600'
                                : 'text-gray-900'
                            }`}
                          >
                            {source.conversion_rate}%
                          </div>
                        </td>
                        <td
                          className="px-3 py-1 whitespace-nowrap cursor-pointer"
                          onClick={() => handleSourceClick(source)}
                        >
                          <div className="font-medium text-[10px] text-gray-900">
                            {formatCompactCurrency(source.revenue)}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
                <tfoot className="sticky bottom-0 bg-gray-50 z-10">
                  <tr
                    className={`border-t-2 border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors ${
                      !selectedSource ? 'bg-blue-50' : 'bg-gray-50'
                    }`}
                    onClick={handleTotalClick}
                  >
                    <td className="pl-3 pr-3 py-2 whitespace-nowrap">
                      <div
                        className={`font-semibold text-[11px] ${
                          !selectedSource ? 'text-blue-600' : 'text-gray-900'
                        }`}
                      >
                        TOTAL
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap cursor-pointer">
                      <div
                        className={`font-semibold text-[11px] ${
                          !selectedSource ? 'text-blue-600' : 'text-gray-900'
                        }`}
                      >
                        {formatCompactNumber(sourceAnalytics.summary.total_sessions)}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap cursor-pointer">
                      <div
                        className={`font-semibold text-[11px] ${
                          !selectedSource ? 'text-blue-600' : 'text-gray-900'
                        }`}
                      >
                        {formatCompactNumber(sourceAnalytics.summary.total_installs)}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap cursor-pointer">
                      <div
                        className={`font-semibold text-[11px] ${
                          !selectedSource ? 'text-blue-600' : 'text-gray-900'
                        }`}
                      >
                        {Math.round(
                          sourceAnalytics.summary.total_sessions *
                            (sourceAnalytics.summary.conversion_rate / 100)
                        ).toLocaleString()}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap cursor-pointer">
                      <div
                        className={`font-semibold text-[11px] ${
                          !selectedSource ? 'text-blue-600' : 'text-gray-900'
                        }`}
                      >
                        {sourceAnalytics.summary.conversion_rate}%
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap cursor-pointer">
                      <div
                        className={`font-semibold text-[11px] ${
                          !selectedSource ? 'text-blue-600' : 'text-gray-900'
                        }`}
                      >
                        {formatCompactCurrency(sourceAnalytics.summary.revenue)}
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </div>

        <div className="col-span-12 lg:col-span-8 space-y-6">
          <Card className="border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-800">
                  {selectedCampaign && selectedSource
                    ? `${selectedSource.source} > ${selectedCampaign.name} Trend Analysis`
                    : selectedSource
                      ? `${selectedSource.source} Trend Analysis`
                      : 'Overall Trend Analysis'}{' '}
                  {selectedRangeLabel && (
                    <span className="text-sm text-gray-500 font-normal ml-2">
                      ({selectedRangeLabel})
                    </span>
                  )}
                </h3>
                <div className="flex items-center gap-3">
                  <select
                    value={primaryMetric}
                    onChange={(e) => setPrimaryMetric(e.target.value)}
                    className="pl-3 pr-8 py-1.5 bg-white border rounded-md text-sm"
                  >
                    <option value="revenue">Revenue</option>
                    <option value="sessions">Sessions</option>
                    <option value="conversions">Conversions</option>
                    <option value="installs">Installs</option>
                    <option value="cr">CR</option>
                  </select>
                  <select
                    value={secondaryMetric}
                    onChange={(e) => setSecondaryMetric(e.target.value)}
                    className="pl-3 pr-8 py-1.5 bg-white border rounded-md text-sm"
                  >
                    <option value="none">Secondary: None</option>
                    <option value="revenue">Secondary: Revenue</option>
                    <option value="sessions">Secondary: Sessions</option>
                    <option value="conversions">Secondary: Conversions</option>
                    <option value="installs">Secondary: Installs</option>
                    <option value="cr">Secondary: CR</option>
                  </select>
                </div>
              </div>
              <div className="h-64">
                <LineChart
                  data={chartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: { padding: 0 },
                    interaction: {
                      mode: 'index',
                      intersect: false
                    },
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: function (tooltipItem) {
                            const datasetLabel = tooltipItem.dataset.label || '';
                            let value = tooltipItem.formattedValue;
                            if (datasetLabel.toLowerCase().includes('revenue')) {
                              value =
                                '₺' +
                                Number(tooltipItem.raw).toLocaleString('tr-TR');
                            } else if (datasetLabel.toLowerCase().includes('cr')) {
                              value = Number(tooltipItem.raw).toFixed(2) + '%';
                            }
                            return `${datasetLabel}: ${value}`;
                          }
                        }
                      }
                    },
                    scales: {
                      y: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        title: {
                          display: true,
                          text:
                            primaryMetric === 'revenue'
                              ? 'Revenue (₺)'
                              : primaryMetric === 'cr'
                                ? 'CR (%)'
                                : primaryMetric === 'conversions'
                                  ? 'Conversions'
                                  : 'Sessions',
                          color: '#334155'
                        },
                        ticks: {
                          color: '#334155',
                          callback: function (value) {
                            if (primaryMetric === 'revenue') {
                              return (
                                '₺' + Number(value).toLocaleString('tr-TR')
                              );
                            }
                            if (primaryMetric === 'cr') {
                              return Number(value).toFixed(0) + '%';
                            }
                            return Number(value).toLocaleString('tr-TR');
                          }
                        }
                      },
                      y1: {
                        type: 'linear',
                        display:
                          secondaryMetric !== 'none' &&
                          secondaryMetric !== primaryMetric,
                        position: 'right',
                        title: {
                          display: true,
                          text:
                            secondaryMetric === 'revenue'
                              ? 'Revenue (₺)'
                              : secondaryMetric === 'cr'
                                ? 'CR (%)'
                                : secondaryMetric === 'conversions'
                                  ? 'Conversions'
                                  : 'Sessions',
                          color: '#334155'
                        },
                        ticks: {
                          color: '#334155',
                          callback: function (value) {
                            if (secondaryMetric === 'revenue') {
                              return (
                                '₺' + Number(value).toLocaleString('tr-TR')
                              );
                            }
                            if (secondaryMetric === 'cr') {
                              return Number(value).toFixed(0) + '%';
                            }
                            return Number(value).toLocaleString('tr-TR');
                          }
                        },
                        grid: {
                          drawOnChartArea: false
                        }
                      },
                      y2: { display: false }
                    }
                  }}
                />
              </div>
              {/* Mini monthly charts under Overall Trend Analysis */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                  <div className="p-4">
                    <h4 className="font-medium text-gray-800 mb-2">Sessions by Month</h4>
                    <div className="h-48">
                      <LineChart
                        data={{
                          labels: allMonthlyChartsData.sessions.labels,
                          datasets: [
                            {
                              label: 'Sessions',
                              data: allMonthlyChartsData.sessions.data,
                              borderColor: 'rgb(59, 130, 246)',
                              backgroundColor: 'rgba(59, 130, 246, 0.1)',
                              fill: true,
                              tension: 0.4,
                              borderWidth: 2,
                              pointBackgroundColor: 'rgb(59, 130, 246)',
                              pointBorderColor: '#fff',
                              pointBorderWidth: 2,
                              pointRadius: 2.5
                            }
                          ]
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          layout: { padding: 0 },
                          plugins: { legend: { display: false } },
                          scales: { y: { beginAtZero: true }, x: {} }
                        }}
                        height="100%"
                      />
                    </div>
                  </div>
                </Card>
                <Card className="border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                  <div className="p-4">
                    <h4 className="font-medium text-gray-800 mb-2">Purchases by Month</h4>
                    <div className="h-48">
                      <LineChart
                        data={{
                          labels: allMonthlyChartsData.purchases.labels,
                          datasets: [
                            {
                              label: 'Purchases',
                              data: allMonthlyChartsData.purchases.data,
                              borderColor: 'rgb(16, 185, 129)',
                              backgroundColor: 'rgba(16, 185, 129, 0.1)',
                              fill: true,
                              tension: 0.4,
                              borderWidth: 2,
                              pointBackgroundColor: 'rgb(16, 185, 129)',
                              pointBorderColor: '#fff',
                              pointBorderWidth: 2,
                              pointRadius: 2.5
                            }
                          ]
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          layout: { padding: 0 },
                          plugins: { legend: { display: false } },
                          scales: { y: { beginAtZero: true }, x: {} }
                        }}
                        height="100%"
                      />
                    </div>
                  </div>
                </Card>
                <Card className="border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                  <div className="p-4">
                    <h4 className="font-medium text-gray-800 mb-2">Revenue by Month</h4>
                    <div className="h-48">
                      <LineChart
                        data={{
                          labels: allMonthlyChartsData.revenue.labels,
                          datasets: [
                            {
                              label: 'Revenue',
                              data: allMonthlyChartsData.revenue.data,
                              borderColor: 'rgb(147, 51, 234)',
                              backgroundColor: 'rgba(147, 51, 234, 0.1)',
                              fill: true,
                              tension: 0.4,
                              borderWidth: 2,
                              pointBackgroundColor: 'rgb(147, 51, 234)',
                              pointBorderColor: '#fff',
                              pointBorderWidth: 2,
                              pointRadius: 2.5
                            }
                          ]
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          layout: { padding: 0 },
                          plugins: { legend: { display: false } },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                callback: function (v) {
                                  return '₺' + Number(v).toLocaleString('tr-TR');
                                }
                              }
                            },
                            x: {}
                          }
                        }}
                        height="100%"
                      />
                    </div>
                  </div>
                </Card>
                <Card className="border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                  <div className="p-4">
                    <h4 className="font-medium text-gray-800 mb-2">CR by Month</h4>
                    <div className="h-48">
                      <LineChart
                        data={{
                          labels: allMonthlyChartsData.conversionRate.labels,
                          datasets: [
                            {
                              label: 'Conversion Rate',
                              data: allMonthlyChartsData.conversionRate.data,
                              borderColor: 'rgb(245, 101, 101)',
                              backgroundColor: 'rgba(245, 101, 101, 0.1)',
                              fill: true,
                              tension: 0.4,
                              borderWidth: 2,
                              pointBackgroundColor: 'rgb(245, 101, 101)',
                              pointBorderColor: '#fff',
                              pointBorderWidth: 2,
                              pointRadius: 2.5
                            }
                          ]
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          layout: { padding: 0 },
                          plugins: { legend: { display: false } },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                callback: function (v) {
                                  return v + '%';
                                }
                              }
                            },
                            x: {}
                          }
                        }}
                        height="100%"
                      />
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Custom Date Range Modal */}
      {isCustomDateOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-96 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Select Custom Date Range
              </h3>
              <button
                onClick={() => setIsCustomDateOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Range
                </label>
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <DatePicker
                    selectsRange
                    startDate={customStartDate}
                    endDate={customEndDate}
                    onChange={(dates) => {
                      const [start, end] = dates || [];
                      setCustomStartDate(start);
                      setCustomEndDate(end);
                    }}
                    inline
                    maxDate={new Date()}
                  />
                </div>
              </div>

              {customStartDate && customEndDate && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    Selected range:{' '}
                    <span className="font-medium">
                      {customStartDate.toLocaleDateString('tr-TR')} -{' '}
                      {customEndDate.toLocaleDateString('tr-TR')}
                    </span>
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleCustomDateReset}
                className="flex-1 px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Reset
              </button>
              <button
                onClick={handleCustomDateApply}
                disabled={!customStartDate || !customEndDate}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


